/* eslint-disable no-unused-expressions */
/* eslint-disable no-shadow */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import { getCookie, setCookie } from '@src/utils/cookie';
import {
  DOCUMENT_LINK,
  LANDING_PAGE_URL,
  SUPPORT_CENTER_URL,
  VBEE_DOMAIN,
  VBEE_TERMS,
} from '@src/configs';
import apis from '@src/apis';
import i18n from '@src/languages';
import { TIME_STORE_LANGUAGE } from '@src/constants/device';
import { COUNTRY, LANGUAGE } from '@src/constants';
import ROUTES from '@src/constants/route';
import {
  LogoutOutlined,
  RadioButtonChecked,
  RadioButtonUnchecked,
  KeyboardArrowRight,
  KeyboardArrowDownRounded,
  ChevronRightRounded,
} from '@mui/icons-material';
import {
  Box,
  Collapse,
  List,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { COLOR } from '@src/styles/color';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import CustomTypography from '@src/new-components/Typography';
import { checkUserHasProvidedEmail } from '@src/services/email';
import actions from '@src/redux/actions';
import useLocation from '@src/hooks/useLocation';
import { clearMoengageIdentity } from '@src/services/moengage';
import { datadogRum } from '@datadog/browser-rum';
import {
  StyledUserActions,
  StyledUserSubActions,
  StyledPopper,
  StyledSubMenu,
} from './index.style';

const languages = [
  {
    value: LANGUAGE.VN,
    country: COUNTRY.VN,
    label: 'vietnamese',
    icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/round/vn.png',
  },
  {
    value: LANGUAGE.EN,
    country: COUNTRY.EN,
    label: 'english',
    icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/round/gb.png',
  },
  // {
  //   value: LANGUAGE.PH,
  //   country: COUNTRY.PH,
  //   label: 'filipino',
  //   icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/Philippines.png',
  // },
  // {
  //   value: LANGUAGE.TH,
  //   country: COUNTRY.TH,
  //   label: 'thailand',
  //   icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/Thailand.png',
  // },
  // {
  //   value: LANGUAGE.CN,
  //   country: COUNTRY.CN,
  //   label: 'chinese',
  //   icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/China.png',
  // },
];

const PopperHelp = ({ openHelpAnchor, setOpenHelpAnchor }) => {
  const { t } = useTranslation();
  return (
    <StyledPopper
      open={Boolean(openHelpAnchor)}
      placement="right-start"
      anchorEl={openHelpAnchor}
      onClose={() => setOpenHelpAnchor(null)}
      style={{ zIndex: 20000 }}
      modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
    >
      <StyledUserSubActions>
        <a
          href={SUPPORT_CENTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-link"
        >
          <MenuItem className="item">{t('userGuide')}</MenuItem>
        </a>
        <a className="text-link" href={`${LANDING_PAGE_URL}/support`}>
          <MenuItem className="item">
            <Typography>{t('supportPage')}</Typography>
          </MenuItem>
        </a>
        {/* <a className="text-link" href={`tel:${VBEE_HOTLINE}`}>
          <MenuItem className="item">
            <CallOutlined style={{ opacity: 0.8 }} />
            <Typography>
              {t('hotline')}: {VBEE_HOTLINE}
            </Typography>
          </MenuItem>
        </a> */}
      </StyledUserSubActions>
    </StyledPopper>
  );
};

const PopperLanguage = ({
  openLanguageAnchor,
  setOpenLanguageAnchor,
  language,
  setLanguage,
  onClose,
}) => {
  const { t } = useTranslation();

  const handleChangeLanguage = (lng) => {
    i18n.changeLanguage(lng.value);
    setLanguage(lng);
  };

  const handleClickChangeLanguage = (lng) => {
    handleChangeLanguage(lng);
    setCookie({
      cname: 'lng',
      cvalue: lng.value,
      domain: VBEE_DOMAIN,
      extime: TIME_STORE_LANGUAGE,
    });
    if (onClose) onClose();
  };

  return (
    <StyledPopper
      open={Boolean(openLanguageAnchor)}
      placement="right-start"
      anchorEl={openLanguageAnchor}
      onClose={() => setOpenLanguageAnchor(null)}
      style={{ zIndex: 20000 }}
      modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
    >
      <StyledUserSubActions>
        {languages.map((lng) => (
          <MenuItem
            className={`item justify-content-between mb-4 ${
              lng.value === language.value ? '' : 'unselected-language'
            }`}
            onClick={() => handleClickChangeLanguage(lng)}
          >
            <Box display="flex" gap="12px" alignItems="center">
              {lng.value === language.value ? (
                <RadioButtonChecked style={{ opacity: 0.8 }} />
              ) : (
                <RadioButtonUnchecked color={COLOR.label[80]} />
              )}
              {t(lng.label)}
            </Box>
            <img src={lng.icon} style={{ height: 24 }} alt="language" />
          </MenuItem>
        ))}
      </StyledUserSubActions>
    </StyledPopper>
  );
};

const UserActions = ({ location, onShowProfile }) => {
  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();
  const isDubbingRoute = useCheckDubbingRoute();
  const { t } = useTranslation();
  const history = useHistory();
  const { ip } = useLocation();
  const { keycloak, removeKcTokenCookie } = useCustomKeycloak();
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [language, setLanguage] = useState({});
  const [openLanguageAnchor, setOpenLanguageAnchor] = useState(null);
  const [openHelpAnchor, setOpenHelpAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector((state) => state.auth);
  const { usingPackage } = useSelector((state) => state.user);
  const { fingerprint: reduxFingerprint } = useSelector(
    (state) => state.deviceV2,
  );

  const useDatadog = getFeatureValue(FEATURE_KEYS.DATADOG_AIVOICE_WEB_APP, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
    packageCode: user?.packageCode,
    ip,
  });

  const hasProvidedEmail = checkUserHasProvidedEmail(user);

  const handleClickReferral = () => {
    dispatch(actions.user.updateIsOpeningRedeemReferralDialog(true));
  };

  const handleGoToProfile = () => {
    keycloak.accountManagement();
  };

  const handleLogout = async () => {
    const fingerprint =
      reduxFingerprint || localStorage.getItem('fingerprint-v2');
    await apis.devices.deactivateDevice(fingerprint);
    removeKcTokenCookie();
    clearMoengageIdentity();
    keycloak.logout({ redirectUri: LANDING_PAGE_URL, fingerprint });
    if (useDatadog?.enabled) {
      datadogRum.stopSessionReplayRecording();
      datadogRum.stopSession();
      datadogRum.clearUser();
    }
  };

  const handleHoverToLanguage = (e) => {
    if (!openLanguageAnchor && !isMobile)
      setOpenLanguageAnchor(e.currentTarget);
    if (openHelpAnchor) setOpenHelpAnchor(null);
  };

  const handleHoverToHelp = (e) => {
    if (!openHelpAnchor && !isMobile) setOpenHelpAnchor(e.currentTarget);
    if (openLanguageAnchor) setOpenLanguageAnchor(null);
  };

  const handleHoverToTheOther = () => {
    if (openHelpAnchor) setOpenHelpAnchor(null);
    if (openLanguageAnchor) setOpenLanguageAnchor(null);
  };

  const handleChangeLanguage = (lng) => {
    i18n.changeLanguage(lng.value);
    setLanguage(lng);

    const languageIcons = document.querySelectorAll(
      '.language-icon-new-profile',
    );
    languageIcons.forEach((lngIcon) => {
      if (lngIcon.dataset.value === lng.value) {
        // eslint-disable-next-line no-param-reassign
        lngIcon.style.filter = 'none';
      } else {
        // eslint-disable-next-line no-param-reassign
        lngIcon.style.filter = 'grayscale(0.9)';
      }
    });
  };

  const handleClickChangeLanguage = (lng) => {
    handleChangeLanguage(lng);
    setCookie({
      cname: 'lng',
      cvalue: lng.value,
      domain: VBEE_DOMAIN,
      extime: TIME_STORE_LANGUAGE,
    });
  };

  const renderNotVeriedEmail = () => {
    if (hasProvidedEmail) {
      return null;
    }

    return (
      <CustomTypography
        variant="body-12-medium"
        className="not-verified-email"
        color={COLOR.orangeV2[100]}
      >
        {t('notVerifiedEmail')}
      </CustomTypography>
    );
  };

  const renderMenuItem = ({
    icon,
    label,
    onClick,
    onMouseEnter,
    justifyContentBetween,
    className,
    dataId,
    dataCurrentPackageCode,
  }) => (
    <MenuItem
      className={`user-action-item ${
        justifyContentBetween ? 'justify-content-between' : ''
      } ${className}`}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      data-id={dataId}
      data-current-package-code={dataCurrentPackageCode}
    >
      {icon}
      {label}
    </MenuItem>
  );

  const renderExternalLink = ({ icon, label, url }) => (
    <MenuItem className="user-action-item" onMouseEnter={handleHoverToTheOther}>
      {icon}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-link"
      >
        {t(label)}
      </a>
    </MenuItem>
  );

  useEffect(() => {
    const defaultLanguageValue = localStorage.getItem('i18nextLng');
    const defaultLanguage = languages.find(
      (lng) => lng.value === defaultLanguageValue,
    );

    const currentLanguageValue = getCookie('lng');
    const currentLanguage = languages.find(
      (lng) => lng.value === currentLanguageValue,
    );

    if (location) {
      const { country: countryCode } = location;
      localStorage.setItem('country', COUNTRY[countryCode] || COUNTRY.EN);
    }

    if (currentLanguage) {
      handleChangeLanguage(currentLanguage);
      return;
    }

    const countryLanguageValue = localStorage.getItem('country');
    const countryLanguage = languages.find(
      (lng) => lng.country === countryLanguageValue,
    );
    if (countryLanguage) {
      handleChangeLanguage(countryLanguage);
      return;
    }

    if (defaultLanguage) {
      setLanguage(defaultLanguage);
      return;
    }
    handleChangeLanguage(languages[0]);
  }, [location]);

  const NEW_SUPPORT_MENU = [
    {
      label: 'userGuide',
      href: DOCUMENT_LINK,
      isActive: true,
    },
    {
      label: 'supportPage',
      href: `${LANDING_PAGE_URL}/support`,
      isActive: true,
    },
  ];

  const handleCloseSidebar = () =>
    dispatch(actions.layout.handleChangeOpenSidebar(false));

  const renderSubMenuSupport = ({ className, itemClassName }) => (
    <StyledSubMenu className={className}>
      <List component="div" disablePadding>
        {NEW_SUPPORT_MENU.filter((item) => item.isActive).map((item) => (
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`item-link ${itemClassName}`}
            onClick={() => handleCloseSidebar()}
          >
            <Typography className="item-text">{t(item.label)}</Typography>
          </a>
        ))}
      </List>
    </StyledSubMenu>
  );

  return (
    <StyledUserActions isMobile={isMobile}>
      <Box className="items-container">
        {renderMenuItem({
          icon: (
            <Box className="user-action-has-referral">
              {t('redeemYourCode')}
            </Box>
          ),
          onMouseEnter: handleHoverToTheOther,
          justifyContentBetween: true,
          onClick: handleClickReferral,
        })}
        {renderMenuItem({
          label: (
            <Box className="verify-email-container">
              {t('profile')}
              {renderNotVeriedEmail()}
            </Box>
          ),
          onClick: handleGoToProfile,
          onMouseEnter: handleHoverToTheOther,
        })}
        {renderMenuItem({
          label: t('manageDevice'),
          onClick: () => {
            history.push(ROUTES.MANAGE_DEVICE);
            if (isMobile) {
              onShowProfile();
            }
          },
          onMouseEnter: handleHoverToTheOther,
        })}
        {renderMenuItem({
          label: t('myPackages'),
          onClick: () => {
            isDubbingRoute
              ? history.push(ROUTES.SUBSCRIPTION_DUBBING)
              : history.push(ROUTES.SUBSCRIPTION);
            if (isMobile) {
              onShowProfile();
            }
          },
          onMouseEnter: handleHoverToTheOther,
          dataId: 'button-my-plan',
          dataCurrentPackageCode: usingPackage?.code,
        })}
        {renderMenuItem({
          label: t('paymentHistory'),
          onClick: () => {
            isDubbingRoute
              ? history.push(ROUTES.PAYMENT_HISTORY_DUBBING)
              : history.push(ROUTES.PAYMENT_HISTORY);
            if (isMobile) {
              onShowProfile();
            }
          },
          onMouseEnter: handleHoverToTheOther,
          dataId: 'button-payment-history',
          dataCurrentPackageCode: usingPackage?.code,
        })}
      </Box>
      <Box className="items-container">
        {renderMenuItem({
          label: t('affiliate'),
          onClick: () => {
            history.push(ROUTES.AFFILIATE);
            if (isMobile) {
              onShowProfile();
            }
          },
          onMouseEnter: handleHoverToTheOther,
        })}
        {renderMenuItem({
          label: t('mobileApp'),
          onClick: () => {
            history.push(ROUTES.MOBILE_APP);
            if (isMobile) {
              onShowProfile();
            }
          },
          onMouseEnter: handleHoverToTheOther,
        })}
      </Box>
      <Box className="items-container">
        {renderExternalLink({
          label: 'termOfUse',
          url: VBEE_TERMS,
        })}
        {isMobile && (
          <Box marginY={1}>
            <Box
              className="item-link"
              onClick={() => setOpenSubMenu((prev) => !prev)}
            >
              <Box className="item-wrapper">
                <Typography className="item-text">{t('support')}</Typography>
                {openSubMenu ? (
                  <KeyboardArrowDownRounded className="item-icon" />
                ) : (
                  <ChevronRightRounded className="item-icon" />
                )}
              </Box>
            </Box>
          </Box>
        )}
        <Collapse in={openSubMenu} timeout="auto" unmountOnExit>
          {renderSubMenuSupport({ itemClassName: 'sub-menu-item' })}
        </Collapse>
        {!isMobile &&
          renderMenuItem({
            icon: <Box className="user-action-has-submenu">{t('support')}</Box>,
            label: <KeyboardArrowRight />,
            onMouseEnter: handleHoverToHelp,
            justifyContentBetween: true,
          })}
        {renderMenuItem({
          icon: <Box className="user-action-has-submenu">{t('language')}</Box>,
          onMouseEnter: handleHoverToLanguage,
          label: isMobile ? (
            <Box className="language-choose-container">
              {languages.map((lng) => (
                <img
                  key={lng.value}
                  data-value={lng.value}
                  onClick={() => handleClickChangeLanguage(lng)}
                  src={lng.icon}
                  alt="language"
                  style={{
                    height: 24,
                    filter:
                      language.value === lng.value ? 'none' : 'grayscale(0.9)',
                  }}
                  className="language-icon-new-profile"
                />
              ))}
            </Box>
          ) : (
            <Box display="flex" alignItems="center">
              <img style={{ height: 24 }} src={language.icon} alt="language" />
              <KeyboardArrowRight />
            </Box>
          ),
          justifyContentBetween: true,
        })}
      </Box>
      <Box className="logout-items-container">
        {renderMenuItem({
          icon: <LogoutOutlined />,
          label: t('logout'),
          className: 'red-color',
          onClick: handleLogout,
          onMouseEnter: handleHoverToTheOther,
        })}
      </Box>
      {openHelpAnchor && (
        <PopperHelp
          openHelpAnchor={openHelpAnchor}
          setOpenHelpAnchor={setOpenHelpAnchor}
        />
      )}
      {openLanguageAnchor && (
        <PopperLanguage
          openLanguageAnchor={openLanguageAnchor}
          setOpenLanguageAnchor={setOpenLanguageAnchor}
          language={language}
          setLanguage={setLanguage}
          onClose={handleHoverToTheOther}
        />
      )}
    </StyledUserActions>
  );
};

export default UserActions;
